import { useIntl } from 'react-intl';

import { Button, FontSizes, FontWeights, Text } from '@calm-web/design-system';

import calmQrCode from '@/../public/_n/images/onboarding/handoff_skipped_qr.png';
import bannerImg from '@/../public/_n/images/pathway-banner.png';
import Image from '@/components/image/NextImage';
import { StoreButton } from '@/components/ui/StoreButton';
import { useAnalytics } from '@/hooks/analytics';
import { useIsMobile } from '@/hooks/ui/useIsMobile';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { CALM_ONE_LINKS } from '@/utils/deeplinks/deeplinks';
import getUrl from '@/utils/getUrl';

import { SignupScreenProps } from '../shared/types';
import messages from './messages';
import {
	ButtonGroup,
	CalmLogoInline,
	HeadingWrapper,
	PathwaysWrapper,
	PhoneWrapper,
	QrCodeText,
	Spacer,
	StoreButtonsWrapper,
} from './styles';

const PathwaysSuccess = ({
	partnerDetails: { id: partner_id, category, user_id_descriptor, integration_type },
}: Pick<SignupScreenProps, 'partnerDetails'>) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const [isMobile] = useIsMobile();

	useComponentDidMount(() => {
		logEvent({
			eventName: 'Pathways Success : Landed',
			eventProps: {
				partner_id,
				integration_type,
				...(category && { partner_type: [category] }),
				...(user_id_descriptor && { user_id_descriptor }),
			},
		});
	});

	const downloadClicked = () => {
		logEvent({ eventName: 'Pathways Success : Download Clicked' });
	};
	const maybeLaterClicked = () => {
		logEvent({ eventName: 'Pathways Success : Maybe Later Clicked' });
	};

	return (
		<PathwaysWrapper isMobile={isMobile}>
			{!isMobile && <Spacer />}
			<HeadingWrapper>
				<Text el="h1" noMargin size={FontSizes['2xl']} weight={FontWeights.Medium}>
					{formatMessage(messages.pathwaysWelcomeHeader, {
						Calm: <CalmLogoInline title="Calm" />,
					})}
				</Text>
			</HeadingWrapper>
			<div>
				<Text el="p" weight={FontWeights.Medium}>
					{formatMessage(messages.pathwaysComingUp)}
				</Text>
				<Text el="p">
					{formatMessage(isMobile ? messages.pathwaysDownloadCopyShort : messages.pathwaysDownloadCopy)}
				</Text>
			</div>
			<Image
				src={bannerImg}
				alt=""
				aria-hidden={true}
				width={294}
				height={200}
				style={{ objectFit: 'contain' }}
			/>

			{isMobile ? (
				<ButtonGroup>
					<Button
						backgroundColor="gradientCalmBrandDark"
						textColor="white"
						fullWidth
						onPress={downloadClicked}
						href={CALM_ONE_LINKS.app}
					>
						{formatMessage(messages.pathwaysDownloadCta)}
					</Button>
					<Button
						backgroundColor="transparent"
						textColor="gray7"
						fullWidth
						href={getUrl('www')}
						onPress={maybeLaterClicked}
					>
						{formatMessage(messages.pathwaysMaybeLaterCta)}
					</Button>
				</ButtonGroup>
			) : (
				<>
					<StoreButtonsWrapper>
						<StoreButton type="ios" />
						<StoreButton type="android" />
					</StoreButtonsWrapper>
					<PhoneWrapper>
						<QrCodeText el="p" weight={FontWeights.Demi}>
							{formatMessage(messages.pathwaysQrCodeCopy)}
						</QrCodeText>
						<Image src={calmQrCode} alt="QR Code" width={149} height={149} style={{ objectFit: 'contain' }} />
					</PhoneWrapper>
				</>
			)}
		</PathwaysWrapper>
	);
};

export default PathwaysSuccess;
